import React from 'react'
import styled, { css } from 'styled-components'

import { useTranslation } from '@root/shared/localization'
import { Gear2Icon, SwapIcon, WalletIcon } from '@root/shared/ui-kit'

import type { InstantTradeTab } from '../atoms'

type Props = {
  active: InstantTradeTab | undefined
  onTabChange: (tab: InstantTradeTab) => void
} & React.HTMLAttributes<HTMLElement>

export const InstantTradeDialogTabs = React.forwardRef<
  React.ElementRef<typeof Root>,
  Props
>(({ active, onTabChange, ...props }, forwardedRef) => {
  const { t } = useTranslation()

  return (
    <Root {...props} ref={forwardedRef}>
      <Tab
        type="button"
        onClick={() => onTabChange('swap')}
        $isActive={active === 'swap'}
      >
        <Label aria-current={active === 'swap'}>
          <SwapIcon size={16} /> {t('swap')}
        </Label>
      </Tab>
      <Tab
        type="button"
        onClick={() => onTabChange('wallets')}
        $isActive={active === 'wallets'}
      >
        <Label aria-current={active === 'wallets'}>
          <WalletIcon size={16} /> {t('trading.navigation.wallets')}
        </Label>
      </Tab>
      <Tab
        type="button"
        onClick={() => onTabChange('settings')}
        $isActive={active === 'settings'}
      >
        <Label aria-current={active === 'settings'}>
          <Gear2Icon size={16} /> {t('settings')}
        </Label>
      </Tab>
    </Root>
  )
})

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  cursor: grab;
`

const Tab = styled.button<{ $isActive: boolean }>`
  all: unset;
  border: 1px solid transparent;

  position: relative;
  cursor: pointer;

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;

  display: grid;
  grid-template-columns: auto 1fr;

  ${({ $isActive }) =>
    $isActive &&
    css`
      border: 1px solid var(--background-5);

      background: var(--background-2);
      border-bottom: none;

      color: var(--text-12);

      &:before {
        content: '';

        position: absolute;
        left: -13px;
        right: -13px;
        height: 15px;
        z-index: 1;
        top: 16.5px;

        --r: 12px;
        background: var(--background-5);
        border-inline: var(--r) solid var(--background-5);

        mask:
          radial-gradient(100% 100% at 0 0, #0000 98%, #000) 0 100% / var(--r)
            var(--r),
          radial-gradient(100% 100% at 100% 0, #0000 98%, #000) 100% 100% /
            var(--r) var(--r),
          linear-gradient(#000 0 0) padding-box;
        mask-repeat: no-repeat;
      }

      &:after {
        content: '';

        position: absolute;
        left: -12px;
        right: -12px;
        height: 16px;
        z-index: 2;
        top: 16.4px;

        --r: 12px;
        border-inline: var(--r) solid var(--background-2);
        background: var(--background-2);

        mask:
          radial-gradient(100% 100% at 0 0, #0000 98%, #000) 0 100% / var(--r)
            var(--r),
          radial-gradient(100% 100% at 100% 0, #0000 98%, #000) 100% 100% /
            var(--r) var(--r),
          linear-gradient(#000 0 0) padding-box;
        mask-repeat: no-repeat;
      }
    `}

  &:first-of-type {
    &:before {
      border-left-width: 0;
      left: -1px;
    }
    &:after {
      border-left-width: 0;
      left: 0px;
    }
  }
`

const Label = styled.span`
  color: var(--text-11);
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px;

  position: relative;
  z-index: 3;

  padding: 7px 12px;
  border-radius: 11px;

  &[aria-current='true'] {
    color: var(--background-12);
    background: var(--background-2);
  }

  &:hover {
    color: var(--text-12);
  }
`
